// ─────────────────────────────────────────────────────────────────────────────
//  services/commands.js — Domain command callers
//
//  Each function:
//    1. Builds the payload the corresponding commands.* function expects
//    2. Generates an idempotency key
//    3. Calls rpc()
//    4. Returns the response envelope
//
//  The frozen two-tier architecture: JS engine computes accounting math (WAC,
//  COGS, totals), passes the results in the payload; commands persist them
//  structurally and enforce integrity (idempotency, permissions, tenancy).
// ─────────────────────────────────────────────────────────────────────────────
import { rpc, idempotencyKey } from './rpc';

// ═══════════════════════════════════ BUSINESS ════════════════════════════════

export async function createBusiness({ name, country = 'TZ' }, token) {
  return rpc('create_business', idempotencyKey('biz'), { name, country }, token);
}

// ═══════════════════════════════════ MASTER DATA ═════════════════════════════

export async function upsertProduct(businessId, product, token, rowVersion) {
  const payload = { business_id: businessId, product };
  if (rowVersion != null) payload.row_version = rowVersion;
  return rpc('upsert_product', idempotencyKey('prod'), payload, token);
}

export async function upsertCustomer(businessId, customer, token, rowVersion) {
  const payload = { business_id: businessId, customer };
  if (rowVersion != null) payload.row_version = rowVersion;
  return rpc('upsert_customer', idempotencyKey('cust'), payload, token);
}

export async function upsertSupplier(businessId, supplier, token, rowVersion) {
  const payload = { business_id: businessId, supplier };
  if (rowVersion != null) payload.row_version = rowVersion;
  return rpc('upsert_supplier', idempotencyKey('supp'), payload, token);
}

export async function upsertExpenseCategory(businessId, category, token, rowVersion) {
  const payload = { business_id: businessId, category };
  if (rowVersion != null) payload.row_version = rowVersion;
  return rpc('upsert_expense_category', idempotencyKey('ecat'), payload, token);
}

// Service payload uses: { name, default_price, default_cost }
export async function upsertService(businessId, service, token, rowVersion) {
  const payload = { business_id: businessId, service };
  if (rowVersion != null) payload.row_version = rowVersion;
  return rpc('upsert_service', idempotencyKey('svc'), payload, token);
}

export async function upsertPaymentAccount(businessId, account, token, rowVersion) {
  const payload = { business_id: businessId, account };
  if (rowVersion != null) payload.row_version = rowVersion;
  return rpc('upsert_payment_account', idempotencyKey('acct'), payload, token);
}

export async function archiveMaster(businessId, kind, id, token) {
  return rpc('archive_master', idempotencyKey('arch'), { business_id: businessId, kind, id }, token);
}

// ═══════════════════════════════════ PERMISSIONS ════════════════════════════

export async function updateRolePermission(businessId, roleKey, permissionKey, granted, token) {
  return rpc('update_role_permission', idempotencyKey('perm'), {
    business_id: businessId, role_key: roleKey,
    permission_key: permissionKey, granted,
  }, token);
}

// ═══════════════════════════════════ SALES ═══════════════════════════════════

/**
 * Create a sale in one shot (draft → posted in the same command).
 * Used when the UI doesn't need a separate draft step.
 *
 * @param {string} businessId
 * @param {object} sale — { sale_date, customer_id?, customer_name?, total_revenue, total_cost, profit, is_paid }
 * @param {Array}  items — [{ item_type, product_id?, service_id?, product_name, description?, quantity, unit_cost, selling_price }]
 * @param {Array}  movements — [{ product_id, qty_delta, unit_cost, movement_value, accounting_date? }]
 * @param {Array}  payments — [{ payment_account_id, wallet_type, amount, payment_method, payment_date }]
 * @param {string} token
 */
export async function createSale(businessId, sale, items, movements, payments, token) {
  return rpc('create_sale', idempotencyKey('sale'), {
    business_id: businessId,
    sale,
    items: items || [],
    movements: movements || [],
    payments: payments || [],
  }, token);
}

export async function createSaleDraft(businessId, sale, items, token) {
  return rpc('create_sale_draft', idempotencyKey('sd'), {
    business_id: businessId,
    sale,
    items: items || [],
  }, token);
}

export async function updateSaleDraft(businessId, saleId, sale, items, rowVersion, token) {
  return rpc('update_sale_draft', idempotencyKey('su'), {
    business_id: businessId,
    sale_id: saleId,
    sale,
    items: items || [],
    row_version: rowVersion,
  }, token);
}

export async function postSale(businessId, saleId, movements, payments, token) {
  return rpc('post_sale', idempotencyKey('sp'), {
    business_id: businessId,
    sale_id: saleId,
    movements: movements || [],
    payments: payments || [],
  }, token);
}

export async function voidSale(businessId, saleId, token) {
  return rpc('void_sale', idempotencyKey('sv'), {
    business_id: businessId,
    sale_id: saleId,
  }, token);
}

export async function recordSalePayment(businessId, saleId, payment, token) {
  return rpc('record_sale_payment', idempotencyKey('spay'), {
    business_id: businessId,
    sale_id: saleId,
    ...payment, // { payment_account_id, wallet_type, amount, payment_method, payment_date }
  }, token);
}

// ═══════════════════════════════════ PURCHASES ═══════════════════════════════

export async function createPurchaseDraft(businessId, purchase, items, token) {
  return rpc('create_purchase_draft', idempotencyKey('pd'), {
    business_id: businessId,
    purchase,
    items: items || [],
  }, token);
}

export async function updatePurchaseDraft(businessId, purchaseId, purchase, items, rowVersion, token) {
  return rpc('update_purchase_draft', idempotencyKey('pu'), {
    business_id: businessId,
    purchase_id: purchaseId,
    purchase,
    items: items || [],
    row_version: rowVersion,
  }, token);
}

export async function postPurchase(businessId, purchaseId, movements, payments, token) {
  return rpc('post_purchase', idempotencyKey('pp'), {
    business_id: businessId,
    purchase_id: purchaseId,
    movements: movements || [],
    payments: payments || [],
  }, token);
}

export async function voidPurchase(businessId, purchaseId, token) {
  return rpc('void_purchase', idempotencyKey('pv'), {
    business_id: businessId,
    purchase_id: purchaseId,
  }, token);
}

export async function recordPurchasePayment(businessId, purchaseId, payment, token) {
  return rpc('record_purchase_payment', idempotencyKey('ppay'), {
    business_id: businessId,
    purchase_id: purchaseId,
    ...payment,
  }, token);
}

// ═══════════════════════════════════ EXPENSES ════════════════════════════════

export async function createExpenseDraft(businessId, expense, token) {
  return rpc('create_expense_draft', idempotencyKey('ed'), {
    business_id: businessId,
    expense,
  }, token);
}

export async function updateExpenseDraft(businessId, expenseId, expense, rowVersion, token) {
  return rpc('update_expense_draft', idempotencyKey('eu'), {
    business_id: businessId,
    expense_id: expenseId,
    expense,
    row_version: rowVersion,
  }, token);
}

export async function postExpense(businessId, expenseId, payment, token) {
  return rpc('post_expense', idempotencyKey('ep'), {
    business_id: businessId,
    expense_id: expenseId,
    payment: payment || null,
  }, token);
}

export async function voidExpense(businessId, expenseId, token) {
  return rpc('void_expense', idempotencyKey('ev'), {
    business_id: businessId,
    expense_id: expenseId,
  }, token);
}

export async function recordExpensePayment(businessId, expenseId, payment, token) {
  return rpc('record_expense_payment', idempotencyKey('epay'), {
    business_id: businessId,
    expense_id: expenseId,
    ...payment,
  }, token);
}

// ═══════════════════════════════════ SERVICES ════════════════════════════════

export async function createServiceTxnDraft(businessId, txn, items, token) {
  return rpc('create_service_txn_draft', idempotencyKey('std'), {
    business_id: businessId,
    txn,
    items: items || [],
  }, token);
}

export async function updateServiceTxnDraft(businessId, txnId, txn, items, rowVersion, token) {
  return rpc('update_service_txn_draft', idempotencyKey('stu'), {
    business_id: businessId,
    txn_id: txnId,
    txn,
    items: items || [],
    row_version: rowVersion,
  }, token);
}

export async function postServiceTxn(businessId, txnId, payments, token) {
  return rpc('post_service_txn', idempotencyKey('stp'), {
    business_id: businessId,
    txn_id: txnId,
    payments: payments || [],
  }, token);
}

export async function voidServiceTxn(businessId, txnId, token) {
  // Key MUST be service_txn_id: commands.void_service_txn reads
  //   v_id uuid := (p_payload->>'service_txn_id')::uuid;
  // Sending txn_id made that NULL, so `WHERE id = NULL` matched no rows and the
  // function raised ERR_VALIDATION on every attempt. (021_write_commands.up.sql:897)
  return rpc('void_service_txn', idempotencyKey('stv'), {
    business_id: businessId,
    service_txn_id: txnId,
  }, token);
}

export async function recordServicePayment(businessId, txnId, payment, token) {
  return rpc('record_service_payment', idempotencyKey('stpay'), {
    business_id: businessId,
    txn_id: txnId,
    ...payment,
  }, token);
}

// ═══════════════════════════════════ RETURNS ═════════════════════════════════

export async function createSalesReturnDraft(businessId, ret, items, token) {
  return rpc('create_sales_return_draft', idempotencyKey('srd'), {
    business_id: businessId,
    ret,
    items: items || [],
  }, token);
}

export async function postSalesReturn(businessId, returnId, movements, token) {
  return rpc('post_sales_return', idempotencyKey('srp'), {
    business_id: businessId,
    return_id: returnId,
    movements: movements || [],
  }, token);
}

export async function voidSalesReturn(businessId, returnId, token) {
  // Key MUST be sales_return_id — commands.void_sales_return reads
  // (p_payload->>'sales_return_id')::uuid. (021_write_commands.up.sql:1038)
  return rpc('void_sales_return', idempotencyKey('srv'), {
    business_id: businessId,
    sales_return_id: returnId,
  }, token);
}

export async function createPurchaseReturnDraft(businessId, ret, items, token) {
  return rpc('create_purchase_return_draft', idempotencyKey('prd'), {
    business_id: businessId,
    ret,
    items: items || [],
  }, token);
}

export async function postPurchaseReturn(businessId, returnId, movements, token) {
  return rpc('post_purchase_return', idempotencyKey('prp'), {
    business_id: businessId,
    return_id: returnId,
    movements: movements || [],
  }, token);
}

export async function voidPurchaseReturn(businessId, returnId, token) {
  // Key MUST be purchase_return_id — commands.void_purchase_return reads
  // (p_payload->>'purchase_return_id')::uuid. (021_write_commands.up.sql:1145)
  return rpc('void_purchase_return', idempotencyKey('prv'), {
    business_id: businessId,
    purchase_return_id: returnId,
  }, token);
}

// ═══════════════════════════════════ INVOICES ════════════════════════════════

export async function createInvoiceDraft(businessId, invoice, items, token) {
  return rpc('create_invoice_draft', idempotencyKey('id'), {
    business_id: businessId,
    invoice,
    items: items || [],
  }, token);
}

export async function postInvoice(businessId, invoiceId, token) {
  return rpc('post_invoice', idempotencyKey('ip'), {
    business_id: businessId,
    invoice_id: invoiceId,
  }, token);
}

export async function voidInvoice(businessId, invoiceId, token) {
  return rpc('void_invoice', idempotencyKey('iv'), {
    business_id: businessId,
    invoice_id: invoiceId,
  }, token);
}

export async function recordInvoiceDeposit(businessId, invoiceId, deposit, token) {
  return rpc('record_invoice_deposit', idempotencyKey('idep'), {
    business_id: businessId,
    invoice_id: invoiceId,
    ...deposit,
  }, token);
}

export async function convertInvoiceToSale(businessId, invoiceId, sale, items, movements, payments, token) {
  return rpc('convert_invoice_to_sale', idempotencyKey('ic'), {
    business_id: businessId,
    invoice_id: invoiceId,
    sale,
    items: items || [],
    movements: movements || [],
    payments: payments || [],
  }, token);
}

// ═══════════════════════════════════ DELETE (hard-delete from DB) ═════════════

export async function deleteSale(businessId, saleId, token) {
  return rpc('delete_sale', idempotencyKey('sdel'), {
    business_id: businessId,
    sale_id: saleId,
  }, token);
}

export async function deletePurchase(businessId, purchaseId, token) {
  return rpc('delete_purchase', idempotencyKey('pdel'), {
    business_id: businessId,
    purchase_id: purchaseId,
  }, token);
}

export async function deleteExpense(businessId, expenseId, token) {
  return rpc('delete_expense', idempotencyKey('edel'), {
    business_id: businessId,
    expense_id: expenseId,
  }, token);
}

export async function deleteServiceTxn(businessId, serviceTxnId, token) {
  return rpc('delete_service_txn', idempotencyKey('stdel'), {
    business_id: businessId,
    service_txn_id: serviceTxnId,
  }, token);
}

export async function deleteSalesReturn(businessId, returnId, token) {
  return rpc('delete_sales_return', idempotencyKey('srdel'), {
    business_id: businessId,
    sales_return_id: returnId,
  }, token);
}

// ═══════════════════════════════════ CASH / WALLET ═══════════════════════════

export async function createCashAdjustment(businessId, adjustment, token) {
  return rpc('create_cash_adjustment', idempotencyKey('cadj'), {
    business_id: businessId,
    ...adjustment, // { account_id, amount, adjustment_type, description, adjustment_date }
  }, token);
}

export async function reverseCashAdjustment(businessId, adjustmentId, reverseDate, token) {
  return rpc('reverse_cash_adjustment', idempotencyKey('crev'), {
    business_id: businessId,
    reversal_of_id: adjustmentId,
    adjustment_date: reverseDate,
  }, token);
}

export async function createWalletTransfer(businessId, transfer, token) {
  return rpc('create_wallet_transfer', idempotencyKey('wt'), {
    business_id: businessId,
    ...transfer, // { from_account_id, to_account_id, amount, transfer_date }
  }, token);
}

export async function reverseWalletTransfer(businessId, transferId, transferDate, token) {
  return rpc('reverse_wallet_transfer', idempotencyKey('wr'), {
    business_id: businessId,
    reversal_of_id: transferId,
    transfer_date: transferDate,
  }, token);
}

// ═══════════════════════════════════ MEMBERSHIP ══════════════════════════════

export async function inviteMember(businessId, email, roleId, tokenHash, token) {
  return rpc('invite_member', idempotencyKey('inv'), {
    business_id: businessId,
    email,
    role_id: roleId,
    token_hash: tokenHash,
  }, token);
}

export async function acceptInvitation(tokenHash, token) {
  return rpc('accept_invitation', idempotencyKey('acc'), {
    token_hash: tokenHash,
  }, token);
}

export async function revokeInvitation(businessId, invitationId, token) {
  return rpc('revoke_invitation', idempotencyKey('rinv'), {
    business_id: businessId,
    invitation_id: invitationId,
  }, token);
}

export async function leaveTeam(businessId, token) {
  return rpc('leave_team', idempotencyKey('leave'), {
    business_id: businessId,
  }, token);
}

export async function changeMemberRole(businessId, memberId, roleId, token) {
  return rpc('change_member_role', idempotencyKey('cmr'), {
    business_id: businessId,
    member_id: memberId,
    role_id: roleId,
  }, token);
}

export async function setMemberStatus(businessId, memberId, status, token) {
  return rpc('set_member_status', idempotencyKey('sms'), {
    business_id: businessId,
    member_id: memberId,
    status,
  }, token);
}

export async function transferOwnership(businessId, fromMemberId, toMemberId, ownerRoleId, demoteRoleId, token) {
  return rpc('transfer_ownership', idempotencyKey('to'), {
    business_id: businessId,
    from_member_id: fromMemberId,
    to_member_id: toMemberId,
    owner_role_id: ownerRoleId,
    demote_role_id: demoteRoleId,
  }, token);
}
